import { UseFormReturnType } from "@mantine/form";
import { GraphQL } from "@rpi/openapi-api";
import Braintree from "braintree-web";
import React from "react";
import { BraintreeFields } from "../../../../hooks/useBraintree";
import { RpiNotification, useRpiNotifications } from "../../../../hooks/useRpiNotifications";
import { isString } from "../../../../utils/type.util";
import { useQueryClient } from "@tanstack/react-query";

export type PaymentFormValues = Omit<
  GraphQL.BraintreePaymentMethodInput,
  "paymentMethodNonce" | "deviceDataFromClient"
> & {
  agreeToTerms: boolean;
};

export const requiredField = (errorMessage: string) => (value: string | undefined) =>
  (value?.length ?? 0) > 0 ? null : errorMessage;

interface UsePaymentFormSubmissionOptions {
  form: UseFormReturnType<PaymentFormValues>;
  hostedFields?: Braintree.HostedFields;
  dataCollector?: Braintree.DataCollector;
  getPayload: () => Promise<{
    payload: Braintree.HostedFieldsTokenizePayload;
    deviceData: string | object;
  }>;
  onComplete: () => void;
}

interface UsePaymentFormSubmissionReturn {
  isLoading: boolean;
  submit: () => Promise<void>;
  cardError: string | undefined;
  braintreeErrors: Partial<Record<BraintreeFields, string>>;
  createPaymentMethod: ReturnType<typeof GraphQL.useCreatePaymentMethodMutation>;
}

export const usePaymentFormSubmission = ({
  form,
  hostedFields,
  dataCollector,
  getPayload,
  onComplete,
}: UsePaymentFormSubmissionOptions): UsePaymentFormSubmissionReturn => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [braintreeErrors, setBraintreeErrors] = React.useState<Partial<Record<BraintreeFields, string>>>({});
  const [cardErrorMessage, setCardErrorMessage] = React.useState<string>();
  // Hide card error once any form field is edited. resetDirty() at error time anchors the
  // dirty baseline to the values that produced the error, so isDirty() flips on first edit.
  const cardError = form.isDirty() ? undefined : cardErrorMessage;
  const { refetch: refetchBulletins } = useRpiNotifications();

  React.useEffect(() => {
    if (!hostedFields) return;

    const clearFieldError = (event: Braintree.HostedFieldsEvent) => {
      const fieldName = event.emittedBy as BraintreeFields;
      setBraintreeErrors((prev) => {
        if (!(fieldName in prev)) return prev;
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
      setCardErrorMessage(undefined);
    };

    const events = ["notEmpty", "empty", "validityChange", "cardTypeChange"] as const;
    events.forEach((event) => hostedFields.on(event, clearFieldError));

    return () => {
      events.forEach((event) => hostedFields.off(event, clearFieldError));
    };
  }, [hostedFields]);

  const createPaymentMethod = GraphQL.useCreatePaymentMethodMutation<{
    message: string;
  }>({
    onSuccess: async (response) => {
      if (response.paymentMethodCreateV2.__typename === "BraintreePaymentMethodRejection") {
        setCardErrorMessage(parsePaymentMethodError(response.paymentMethodCreateV2));
        form.resetDirty();
        setIsSubmitting(false);
      } else {
        refetchBulletins(RpiNotification.PAYMENT_METHOD);
        await queryClient.invalidateQueries({ queryKey: ["ListPaymentMethods"] });
        setIsSubmitting(false);
        onComplete();
      }
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const isLoading = isSubmitting || createPaymentMethod.isLoading;

  const submit = React.useCallback(async () => {
    if (!hostedFields || !dataCollector) return;

    createPaymentMethod.reset();
    setIsSubmitting(true);

    const validationResult = form.validate();

    let braintreeData: { nonce: string; deviceData: string } | undefined;
    try {
      const { payload, deviceData } = await getPayload();
      braintreeData = {
        nonce: payload.nonce,
        deviceData: typeof deviceData === "string" ? deviceData : JSON.stringify(deviceData),
      };
      setBraintreeErrors({});
      setCardErrorMessage(undefined);
    } catch (error) {
      if (isString(error)) {
        setCardErrorMessage(error);
        form.resetDirty();
      } else {
        setBraintreeErrors(error as Partial<Record<BraintreeFields, string>>);
      }
    }

    if (!braintreeData || validationResult.hasErrors) {
      setIsSubmitting(false);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { agreeToTerms, ...input } = form.values;
    createPaymentMethod.mutate({
      input: {
        ...input,
        paymentMethodNonce: braintreeData.nonce,
        deviceDataFromClient: braintreeData.deviceData,
      },
    });
  }, [hostedFields, dataCollector, createPaymentMethod, form, getPayload]);

  return {
    isLoading,
    submit,
    cardError,
    braintreeErrors,
    createPaymentMethod,
  };
};

export function parsePaymentMethodError(
  response: Extract<
    GraphQL.CreatePaymentMethodMutation["paymentMethodCreateV2"],
    { __typename: "BraintreePaymentMethodRejection" }
  >
) {
  if (response.verification?.avsPostalCodeResponseCode === "N") {
    return "Postal code does not match the billing address for the card.";
  }
  if (response.verification?.avsStreetAddressResponseCode === "N") {
    return "Street address does not match the billing address for the card.";
  }

  return "The card was rejected. Ensure all information is correct, or try a different card.";
}
